import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Retail & e-commerce",
  title: "Product enquiry agent",
  cardSummary:
    "Answer product questions instantly on WhatsApp — availability, specs and price — from your own catalogue.",
  heroSubhead:
    "We design, build and deploy a WhatsApp AI agent that answers product questions the moment they land — is it in stock, what are the specs, how much, which model fits — straight from your own catalogue and live stock, so buyers get an answer in seconds instead of drifting to a competitor while your staff are busy.",
  snapshot: {
    valueLabel: "Very high",
    valueWhy:
      "Most product questions arrive on WhatsApp and most buyers won't wait — an instant, accurate answer is the difference between a sale and a lost lead, day and night.",
    complexityLabel: "Low–medium",
    complexityWhy:
      "A routing front-end into a few read-only handlers on a single model; the real work is wiring WhatsApp, your catalogue/stock and your spec content — no vision, no autonomous money movement, no heavy compliance.",
    timeToLive: "~2–4 weeks (one catalogue)",
    bestFit: "Retail & e-commerce SMEs fielding product questions on WhatsApp",
  },
  outcomes: [
    "Turn instant, correct answers into sales you're losing today to slow replies",
    "Capture after-hours & peak enquiries your team can't get to in time",
    "Stop losing trust to wrong prices and phantom stock — answers come from live data",
    "Free your team from repeating the same ten answers all day",
  ],
  impact: {
    manualLabel: "Answering an enquiry by hand",
    manualValue: "~3–8 min (often much later)",
    agentLabel: "With the agent",
    agentValue: "~5 sec, instant",
    saved:
      "≈ 3–8 minutes saved per enquiry — about 40–80 hours a month at 800 enquiries. But the real win is revenue: an instant answer converts far better than one that lands an hour later, and at ~RM 0.08 in AI cost, a single recovered sale a day pays for it many times over.",
    note: "Indicative; actual time and lift depend on catalogue size and how many enquiries you get after hours.",
  },
  businessProblem:
    "A retailer or online seller in the region gets a constant stream of 'ada stock?', 'berapa harga?', 'ni boleh guna untuk…?' on WhatsApp. Every one needs a staff member to stop, check the catalogue and the stock, work out which product they mean, and type a reply — while three more messages pile up. At peak it's a bottleneck; after hours it's silence, and by morning the buyer has ordered from someone who answered first. The questions are repetitive, but the answers have to be exactly right — a wrong price or a phantom 'in stock' costs you money and trust.",
  manualWorkflow: {
    steps: [
      "Read the incoming WhatsApp question",
      "Work out which product they actually mean",
      "Check the catalogue for specs and the current price",
      "Check stock — is it actually available?",
      "Type back the answer, and handle the follow-up",
    ],
    breaks:
      "The bottleneck is that every simple question still needs a person to look things up and reply — so answers are slow at peak, absent after hours, and drift out of date whenever price or stock changes.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as a routing workflow: each message is first classified — is this an availability check, a spec question, a price question, a comparison, or something for a human? — then dispatched to the specialised handler for that intent, each answering from your live catalogue and stock, and escalating cleanly to your team when it's a real buying conversation or beyond scope.",
    steps: [
      "Classify the question's intent",
      "Identify the product from the catalogue",
      "Route to the right handler (stock / specs / price / compare)",
      "Answer from live catalogue & stock",
      "Escalate a hot lead or out-of-scope to your team",
    ],
    inLabel: "A customer asks on WhatsApp",
    outLabel: "An instant, correct answer",
    loopAround: {
      index: 0,
      label: "The router re-classifies as the conversation shifts — a spec question can turn into a price then a buy — dispatching each turn to the right handler.",
    },
    humanStep: "A ready-to-buy lead is handed to your team to close",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "Customer message",
    inMessage:
      "bro this air fryer 5.5L ada stock ah? yang warna hitam. price how much? boleh COD ke KL?",
    outLabel: "Instant answer sent",
    outLines: [
      "Air Fryer 5.5L (Black) — in stock, 12 units at your PJ warehouse",
      "Price: RM 249 (was RM 299) — promo ends Sunday",
      "COD available in KL · or delivery 1–2 working days",
    ],
    outResult: "Answered in ~5 sec · buyer asked to checkout → handed to your team",
    note: "Every figure comes from your live catalogue & stock, never guessed · ~RM 0.05–0.10 in AI cost · a hot lead flagged for a human to close.",
  },
  workflowDetail: [
    {
      title: "Classify the intent",
      detail:
        "The agent reads the message — in Malay, English, Chinese or the mix people actually type — and works out what's really being asked: is it available, what are the specs, what's the price, how do two products compare, or is this a human matter like a complaint or a bulk deal.",
    },
    {
      title: "Identify the product",
      detail:
        "It resolves the message to a real item in your catalogue — matching model numbers, nicknames, colours and shorthand — and asks a short clarifying question only when it genuinely can't tell which product is meant.",
    },
    {
      title: "Route to the right handler",
      detail:
        "Based on the intent, it dispatches to the specialised handler — an availability check, a spec lookup, a price/promo answer, or a side-by-side comparison — each tuned for that kind of question rather than one generic reply.",
    },
    {
      title: "Answer from live data",
      detail:
        "It answers from your live catalogue and current stock — the real price, the real specs, whether it's actually in stock and where — so it never quotes an out-of-date price or promises stock that isn't there.",
    },
    {
      title: "Escalate the buying moment",
      detail:
        "When the buyer is ready to order, wants a bulk price, or asks something out of scope, it hands a clean summary of the conversation to your team to close — the enquiry becomes a warm lead, not a dead end.",
    },
    {
      title: "Hand to your team",
      detail:
        "Your team steps in only for the conversations that are worth their time — ready-to-buy leads and genuine edge cases — with the context already gathered.",
    },
  ],
  howWeBuild:
    "Because it answers only from your live catalogue and current stock, it can't quote yesterday's price or promise stock you've already sold — the answer is right or it doesn't send it. It never invents a spec or a discount it can't cite, it never takes payment on its own, and every enquiry and answer is logged, so you can see exactly what customers ask and what the agent told them.",
  agentLoop: [
    "the question + your live catalogue, specs & stock",
    "classify the intent & answer from the catalogue (MCP tools)",
    "check the answer cites real stock, spec & price before sending",
  ],
  integrations: [
    "WhatsApp Business API",
    "Your product catalogue & spec content",
    "Live stock / inventory system",
    "Optional: your CRM or lead inbox for hot leads",
  ],
  architecture: [
    "Pattern: a routing workflow on the Claude Agent SDK — a lightweight classifier dispatches each turn to one of a few specialised handlers (availability / specs / price / compare / escalate), each exposed via an in-process MCP server.",
    "Context engineering: your catalogue schema, spec sheets and promo rules are retrieved per product and prompt-cached; live stock is read at answer-time, never from a stale copy, so the reply reflects what's actually on the shelf right now.",
    "Tools: read-only catalogue, spec and stock lookups plus a comparison helper — all read-only, so there is no path by which an enquiry can change inventory, pricing or an order; the only write is creating a lead record.",
    "Grounding guardrail: answers are constrained to what the tools return — the agent may not state a price, spec or availability it can't cite from live data, which closes off the hallucinated-spec / phantom-stock failure mode.",
    "Untrusted input: customer messages are treated as untrusted (prompt-injection resistant) so a crafted message can't make it leak internal data or invent a discount; names and phone numbers are handled PDPA/PDPC-aligned.",
    "Escalation & recovery: low classifier confidence, a buying signal, or an out-of-scope question routes to a human queue with the conversation summarised — every uncertain path ends at a person, never a confident wrong answer.",
    "Observability & audit: every classification, tool call and answer is logged and traceable, giving you a searchable record of what customers ask and exactly what the agent replied — useful for both compliance and merchandising.",
    "Eval harness: a golden set of real historical enquiry chats — messy 'rojak', mixed-language, ambiguous-product and trick questions — with deterministic checks (right product, right price, right stock state) plus LLM-as-judge on tone and routing, regression-tested on every change before it touches live chats.",
  ],
  modelChoice: {
    approach: "Single model, tiered by task",
    rationale:
      "Routing an enquiry and answering it shares one short context, so a single well-instrumented model with a cheap classifier front-end is the right call — fast, cheap per enquiry, and easy to audit — rather than a multi-agent design.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single question-and-answer exchange, where a Haiku classifier into a Sonnet handler is both cheaper and faster.",
  },
  models: [
    {
      task: "Classify the intent & match the product",
      model: "Claude Haiku",
      why: "Cheap and fast for the bulk of routing and item-matching, which is most of the traffic.",
    },
    {
      task: "Answer specs, price & availability clearly",
      model: "Claude Sonnet",
      why: "Reliable, well-phrased answers on messy mixed-language questions, with the nuance to compare products.",
    },
    {
      task: "Handle a tricky comparison or unusual question",
      model: "Claude Sonnet",
      why: "Takes the harder cases the classifier flags — multi-product comparisons, edge-case fit questions.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per enquiry",
        value: "~RM 0.05–0.10",
        note: "a short classify-and-answer exchange at Claude token rates",
      },
      {
        label: "AI usage — at 3,000 enquiries/month",
        value: "~RM 130–260 / month",
        note: "scales with enquiry volume; the Haiku classifier keeps the bulk cheap",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting WhatsApp, your catalogue and your stock system is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the after-hours enquiries it catches and the sales it recovers",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on enquiry length and monthly volume. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  evaluation: [
    "Deterministic checks first — right product, right current price, right stock state",
    "Self-verification — the answer must cite live catalogue & stock before it's sent",
    "Business KPIs — enquiries answered after hours, answer accuracy, and hot leads passed to your team",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure your current WhatsApp enquiry handling on a sample — how fast a question gets answered, how many are wrong (stale price, wrong stock), and how many after-hours enquiries are missed. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it answer the question correctly?",
        metrics: [
          {
            name: "Answer accuracy vs a human",
            measures: "The reply matches the correct product, price and stock state, on held-out chats",
            target: "≥ 97%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the enquiry routed and handled soundly?",
        metrics: [
          {
            name: "Correct intent routing",
            measures: "Availability / specs / price / compare / escalate classified correctly",
            target: "≥ 95%",
          },
          {
            name: "Hot leads escalated",
            measures: "Ready-to-buy and out-of-scope conversations handed to a human — none dropped",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual answers correct?",
        metrics: [
          {
            name: "Product match accuracy",
            measures: "The message mapped to the right catalogue item",
            target: "≥ 97%",
          },
          {
            name: "Price & stock correctness",
            measures: "Every quoted price and availability matches live data at answer-time",
            target: "≥ 99%",
          },
          {
            name: "No invented specs or stock",
            measures: "Never states a spec, price or availability it can't cite from your data",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent doesn't answer a live customer until it beats the human baseline and clears these GPA targets on a golden set of real historical enquiry chats — including the messy and trick ones. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real WhatsApp enquiry chats, pilot it on one channel or catalogue with staff oversight, then take it live. A focused single-catalogue rollout is a matter of weeks.",
  ctaTitle: "Answering the same product questions all day on WhatsApp?",
  ctaDescription:
    "Tell us your catalogue size and enquiry volume and we'll tell you honestly whether a product enquiry agent is worth building — and what it would take.",
  faqs: [
    {
      question: "How does it avoid quoting a wrong price or fake stock?",
      answer:
        "It answers only from your live catalogue and stock through connected tools, and is constrained so it can't state a price, spec or availability it can't cite from that data. If it isn't sure, it asks or escalates rather than guessing.",
    },
    {
      question: "Does it handle Malay, English and Chinese — and mixed messages?",
      answer:
        "Yes. It's built for how Malaysians actually type — Bahasa Malaysia, English, Chinese and 'rojak' mixes — and answers clearly in the same conversation.",
    },
    {
      question: "Does it take orders or payment?",
      answer:
        "No — this agent answers questions and hands ready-to-buy leads to your team. If you also want it to capture the order end to end, that's our order processing agent, which pairs neatly with this one.",
    },
    {
      question: "What does it connect to?",
      answer:
        "WhatsApp Business, your product catalogue and spec content, and your live stock or inventory system — plus optionally your CRM so hot leads land where your sales team works. We connect it during the build.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Runcit & e-dagang",
  title: "Ejen pertanyaan produk",
  cardSummary:
    "Jawab pertanyaan produk serta-merta di WhatsApp — stok, spesifikasi dan harga — daripada katalog anda.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI WhatsApp yang menjawab pertanyaan produk sebaik ia tiba — ada stok tak, apa spesifikasinya, berapa harga, model mana yang sesuai — terus daripada katalog dan stok langsung anda, supaya pembeli dapat jawapan dalam beberapa saat, bukannya beralih kepada pesaing sementara kakitangan anda sibuk.",
  snapshot: {
    valueLabel: "Sangat tinggi",
    valueWhy:
      "Kebanyakan pertanyaan produk tiba di WhatsApp dan kebanyakan pembeli tidak mahu menunggu — jawapan segera dan tepat ialah beza antara jualan dan prospek yang hilang, siang dan malam.",
    complexityLabel: "Rendah–sederhana",
    complexityWhy:
      "Bahagian hadapan penghalaan ke beberapa pengendali baca-sahaja atas model tunggal; kerja sebenar ialah menyambung WhatsApp, katalog/stok dan kandungan spesifikasi anda — tanpa visi, tanpa pergerakan wang automatik, tanpa pematuhan berat.",
    timeToLive: "~2–4 minggu (satu katalog)",
    bestFit: "PKS runcit & e-dagang yang menerima pertanyaan produk di WhatsApp",
  },
  outcomes: [
    "Tukar jawapan segera dan tepat kepada jualan yang anda hilang hari ini kerana balasan lambat",
    "Rakam pertanyaan selepas waktu & waktu puncak yang pasukan anda tidak sempat layan",
    "Kurangkan harga salah dan stok palsu — jawapan datang daripada data langsung",
    "Bebaskan pasukan anda daripada mengulang sepuluh jawapan yang sama sepanjang hari",
  ],
  impact: {
    manualLabel: "Menjawab pertanyaan secara manual",
    manualValue: "~3–8 min (selalunya jauh lewat)",
    agentLabel: "Dengan ejen",
    agentValue: "~5 saat, serta-merta",
    saved:
      "≈ 3–8 minit dijimatkan setiap pertanyaan — kira-kira 40–80 jam sebulan pada 800 pertanyaan. Tetapi kemenangan sebenar ialah hasil: jawapan segera menukar jauh lebih baik daripada yang tiba sejam kemudian, dan pada ~RM 0.08 kos AI, satu jualan yang diselamatkan sehari sudah membayarnya berkali ganda.",
    note: "Anggaran; masa dan peningkatan sebenar bergantung pada saiz katalog dan berapa banyak pertanyaan selepas waktu.",
  },
  businessProblem:
    "Peruncit atau penjual dalam talian di rantau ini menerima aliran berterusan 'ada stock?', 'berapa harga?', 'ni boleh guna untuk…?' di WhatsApp. Setiap satu memerlukan kakitangan berhenti, menyemak katalog dan stok, memikirkan produk mana yang dimaksudkan, dan menaip balasan — sementara tiga lagi mesej bertimbun. Pada waktu puncak ia halangan; selepas waktu ia sunyi, dan menjelang pagi pembeli sudah memesan daripada orang yang menjawab dahulu. Soalan berulang, tetapi jawapan mesti betul-betul tepat — harga salah atau 'ada stok' palsu merugikan wang dan kepercayaan anda.",
  manualWorkflow: {
    steps: [
      "Baca soalan WhatsApp yang masuk",
      "Fikirkan produk mana yang mereka maksudkan",
      "Semak katalog untuk spesifikasi dan harga semasa",
      "Semak stok — adakah ia benar-benar tersedia?",
      "Taip balasan, dan uruskan susulan",
    ],
    breaks:
      "Halangan ialah setiap soalan mudah masih memerlukan orang untuk menyemak dan membalas — jadi jawapan lambat pada waktu puncak, tiada selepas waktu, dan lapuk setiap kali harga atau stok berubah.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran penghalaan: setiap mesej diklasifikasikan dahulu — adakah ini semakan stok, soalan spesifikasi, soalan harga, perbandingan, atau perkara untuk manusia? — kemudian dihantar ke pengendali khusus untuk niat itu, setiap satu menjawab daripada katalog dan stok langsung anda, dan menyerahkan dengan kemas kepada pasukan anda apabila ia perbualan membeli yang sebenar atau di luar skop.",
    steps: [
      "Klasifikasikan niat soalan",
      "Kenal pasti produk daripada katalog",
      "Halakan ke pengendali betul (stok / spek / harga / banding)",
      "Jawab daripada katalog & stok langsung",
      "Angkat prospek panas atau di luar skop kepada pasukan anda",
    ],
    inLabel: "Pelanggan bertanya di WhatsApp",
    outLabel: "Jawapan segera dan tepat",
    loopAround: {
      index: 0,
      label: "Penghala mengklasifikasi semula apabila perbualan berubah — soalan spek boleh bertukar kepada harga kemudian belian — menghantar setiap giliran ke pengendali yang betul.",
    },
    humanStep: "Prospek sedia-beli diserahkan kepada pasukan anda untuk ditutup",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "Mesej pelanggan",
    inMessage:
      "bro air fryer 5.5L ni ada stock ah? yang warna hitam. price how much? boleh COD ke KL?",
    outLabel: "Jawapan segera dihantar",
    outLines: [
      "Air Fryer 5.5L (Hitam) — ada stok, 12 unit di gudang PJ anda",
      "Harga: RM 249 (dahulu RM 299) — promo tamat Ahad",
      "COD ada di KL · atau penghantaran 1–2 hari bekerja",
    ],
    outResult: "Dijawab dalam ~5 saat · pembeli minta checkout → diserah kepada pasukan anda",
    note: "Setiap angka datang daripada katalog & stok langsung anda, tidak pernah diteka · ~RM 0.05–0.10 kos AI · prospek panas ditandakan untuk manusia tutup.",
  },
  workflowDetail: [
    {
      title: "Klasifikasikan niat",
      detail:
        "Ejen membaca mesej — dalam Bahasa Malaysia, Inggeris, Cina atau campuran yang orang taip — dan memikirkan apa yang sebenarnya ditanya: adakah ia tersedia, apa spesifikasinya, berapa harganya, bagaimana dua produk dibandingkan, atau adakah ini perkara manusia seperti aduan atau tawaran pukal.",
    },
    {
      title: "Kenal pasti produk",
      detail:
        "Ia menyelesaikan mesej kepada item sebenar dalam katalog anda — memadan nombor model, gelaran, warna dan singkatan — dan bertanya soalan penjelasan ringkas hanya apabila ia benar-benar tidak dapat pasti produk mana yang dimaksudkan.",
    },
    {
      title: "Halakan ke pengendali betul",
      detail:
        "Berdasarkan niat, ia menghantar ke pengendali khusus — semakan stok, carian spesifikasi, jawapan harga/promo, atau perbandingan sebelah-menyebelah — setiap satu disesuaikan untuk jenis soalan itu, bukan satu balasan generik.",
    },
    {
      title: "Jawab daripada data langsung",
      detail:
        "Ia menjawab daripada katalog dan stok semasa anda — harga sebenar, spesifikasi sebenar, sama ada ia benar-benar ada stok dan di mana — jadi ia tidak pernah memberi harga lapuk atau menjanjikan stok yang tiada.",
    },
    {
      title: "Angkat detik membeli",
      detail:
        "Apabila pembeli sedia memesan, mahu harga pukal, atau bertanya sesuatu di luar skop, ia menyerahkan ringkasan kemas perbualan kepada pasukan anda untuk ditutup — pertanyaan menjadi prospek hangat, bukan jalan mati.",
    },
    {
      title: "Serah kepada pasukan anda",
      detail:
        "Pasukan anda masuk hanya untuk perbualan yang berbaloi dengan masa mereka — prospek sedia-beli dan kes tepi sebenar — dengan konteks yang sudah dikumpul.",
    },
  ],
  howWeBuild:
    "Kerana ia menjawab hanya daripada katalog dan stok semasa anda, ia tidak boleh memberi harga semalam atau menjanjikan stok yang sudah anda jual — jawapan itu betul atau ia tidak menghantarnya. Ia tidak pernah mereka spesifikasi atau diskaun yang tidak boleh dipetiknya, ia tidak pernah menerima bayaran sendiri, dan setiap pertanyaan serta jawapan dilog, jadi anda boleh lihat tepat apa pelanggan tanya dan apa yang ejen beritahu mereka.",
  agentLoop: [
    "soalan + katalog, spesifikasi & stok langsung anda",
    "klasifikasikan niat & jawab daripada katalog (alat MCP)",
    "semak jawapan memetik stok, spek & harga sebenar sebelum menghantar",
  ],
  integrations: [
    "WhatsApp Business API",
    "Katalog produk & kandungan spesifikasi anda",
    "Sistem stok / inventori langsung",
    "Pilihan: CRM atau peti masuk prospek anda untuk prospek panas",
  ],
  architecture: [
    "Corak: aliran penghalaan atas Claude Agent SDK — pengklasifikasi ringan menghantar setiap giliran ke salah satu daripada beberapa pengendali khusus (stok / spek / harga / banding / angkat), setiap satu didedahkan melalui pelayan MCP dalam-proses.",
    "Kejuruteraan konteks: skema katalog, helaian spesifikasi dan peraturan promo anda diambil bagi setiap produk dan di-cache-prompt; stok langsung dibaca pada masa-jawapan, bukan daripada salinan lapuk, jadi balasan mencerminkan apa yang benar-benar ada di rak sekarang.",
    "Alat: carian katalog, spesifikasi dan stok baca-sahaja serta pembantu perbandingan — semuanya baca-sahaja, jadi tiada laluan di mana pertanyaan boleh menukar inventori, harga atau pesanan; satu-satunya tulis ialah mencipta rekod prospek.",
    "Pagar pengukuhan: jawapan dihadkan kepada apa yang dikembalikan alat — ejen tidak boleh menyatakan harga, spesifikasi atau ketersediaan yang tidak boleh dipetiknya daripada data langsung, yang menutup mod kegagalan spek-halusinasi / stok-hantu.",
    "Input tidak dipercayai: mesej pelanggan dianggap tidak dipercayai (tahan suntikan-prompt) supaya mesej yang direka tidak boleh membuatnya membocorkan data dalaman atau mereka diskaun; nama dan nombor telefon dikendalikan selaras PDPA/PDPC.",
    "Angkat & pemulihan: keyakinan pengklasifikasi rendah, isyarat membeli, atau soalan di luar skop dihalakan ke baris gilir manusia dengan perbualan diringkaskan — setiap laluan tidak pasti berakhir pada manusia, bukan jawapan salah yang yakin.",
    "Kebolehlihatan & audit: setiap klasifikasi, panggilan alat dan jawapan dilog dan boleh dijejak, memberi anda rekod boleh dicari tentang apa pelanggan tanya dan tepat apa yang ejen balas — berguna untuk pematuhan dan perancangan barangan.",
    "Rangka penilaian: set emas chat pertanyaan sejarah sebenar — 'rojak' berselerak, berbilang bahasa, produk samar dan soalan helah — dengan semakan deterministik (produk betul, harga betul, keadaan stok betul) serta LLM-sebagai-hakim untuk nada dan penghalaan, diuji regresi pada setiap perubahan sebelum menyentuh chat langsung.",
  ],
  modelChoice: {
    approach: "Model tunggal, berperingkat mengikut tugas",
    rationale:
      "Menghalakan pertanyaan dan menjawabnya berkongsi satu konteks pendek, jadi model tunggal yang lengkap dengan pengklasifikasi murah di hadapan adalah pilihan tepat — pantas, murah setiap pertanyaan, dan mudah diaudit — bukannya reka bentuk berbilang-ejen.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari dan meluas — bukan untuk satu pertukaran soal-jawab, di mana pengklasifikasi Haiku ke pengendali Sonnet adalah lebih murah dan lebih pantas.",
  },
  models: [
    {
      task: "Klasifikasikan niat & padan produk",
      model: "Claude Haiku",
      why: "Murah dan pantas untuk sebahagian besar penghalaan dan pemadanan item, iaitu majoriti trafik.",
    },
    {
      task: "Jawab spek, harga & ketersediaan dengan jelas",
      model: "Claude Sonnet",
      why: "Jawapan yang boleh dipercayai dan terungkap baik untuk soalan berbilang bahasa yang berselerak, dengan nuansa untuk membanding produk.",
    },
    {
      task: "Kendali perbandingan rumit atau soalan luar biasa",
      model: "Claude Sonnet",
      why: "Mengambil kes lebih sukar yang ditandakan pengklasifikasi — perbandingan berbilang produk, soalan kesesuaian kes tepi.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap pertanyaan",
        value: "~RM 0.05–0.10",
        note: "pertukaran klasifikasi-dan-jawab pendek pada kadar token Claude",
      },
      {
        label: "Penggunaan AI — pada 3,000 pertanyaan/bulan",
        value: "~RM 130–260 / bulan",
        note: "berskala mengikut volum pertanyaan; pengklasifikasi Haiku mengekalkan majoriti murah",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung WhatsApp, katalog dan sistem stok anda adalah beberapa minggu; sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding pertanyaan selepas waktu yang ditangkap dan jualan yang diselamatkan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada panjang pertanyaan dan volum bulanan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  evaluation: [
    "Semakan deterministik dahulu — produk betul, harga semasa betul, keadaan stok betul",
    "Pengesahan sendiri — jawapan mesti memetik katalog & stok langsung sebelum dihantar",
    "KPI perniagaan — pertanyaan dijawab selepas waktu, ketepatan jawapan, dan prospek panas dihantar ke pasukan anda",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur pengendalian pertanyaan WhatsApp semasa anda pada sampel — berapa cepat soalan dijawab, berapa yang salah (harga lapuk, stok salah), dan berapa pertanyaan selepas waktu terlepas. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia menjawab soalan dengan betul?",
        metrics: [
          {
            name: "Ketepatan jawapan vs manusia",
            measures: "Balasan sepadan dengan produk, harga dan keadaan stok yang betul, pada chat terpencil",
            target: "≥ 97%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah pertanyaan dihalakan dan dikendalikan dengan kukuh?",
        metrics: [
          {
            name: "Penghalaan niat betul",
            measures: "Stok / spek / harga / banding / angkat diklasifikasi dengan betul",
            target: "≥ 95%",
          },
          {
            name: "Prospek panas diangkat",
            measures: "Perbualan sedia-beli dan di luar skop diserah kepada manusia — tiada tercicir",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap jawapan betul?",
        metrics: [
          {
            name: "Ketepatan padanan produk",
            measures: "Mesej dipetakan ke item katalog yang betul",
            target: "≥ 97%",
          },
          {
            name: "Ketepatan harga & stok",
            measures: "Setiap harga dan ketersediaan yang disebut sepadan dengan data langsung pada masa-jawapan",
            target: "≥ 99%",
          },
          {
            name: "Tiada spek atau stok direka",
            measures: "Tidak pernah menyatakan spek, harga atau ketersediaan yang tidak boleh dipetiknya daripada data anda",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menjawab pelanggan langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas chat pertanyaan sejarah sebenar — termasuk yang berselerak dan helah. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas chat pertanyaan WhatsApp sebenar anda, menguji pada satu saluran atau katalog dengan pengawasan kakitangan, kemudian melancarkannya. Pelancaran satu katalog yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Menjawab soalan produk yang sama sepanjang hari di WhatsApp?",
  ctaDescription:
    "Beritahu kami saiz katalog dan volum pertanyaan anda, dan kami akan beritahu dengan jujur sama ada ejen pertanyaan produk berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Bagaimana ia mengelak memberi harga salah atau stok palsu?",
      answer:
        "Ia menjawab hanya daripada katalog dan stok langsung anda melalui alat yang disambung, dan dihadkan supaya ia tidak boleh menyatakan harga, spek atau ketersediaan yang tidak boleh dipetiknya daripada data itu. Jika ia tidak pasti, ia bertanya atau mengangkat, bukan meneka.",
    },
    {
      question: "Adakah ia mengendalikan Melayu, Inggeris dan Cina — dan mesej campuran?",
      answer:
        "Ya. Ia dibina untuk cara rakyat Malaysia menaip — Bahasa Malaysia, Inggeris, Cina dan campuran 'rojak' — dan menjawab dengan jelas dalam perbualan yang sama.",
    },
    {
      question: "Adakah ia menerima pesanan atau bayaran?",
      answer:
        "Tidak — ejen ini menjawab soalan dan menyerahkan prospek sedia-beli kepada pasukan anda. Jika anda juga mahu ia merakam pesanan dari mula hingga akhir, itu ialah ejen pemprosesan pesanan kami, yang berpadan kemas dengan yang ini.",
    },
    {
      question: "Apa yang ia sambung?",
      answer:
        "WhatsApp Business, katalog produk dan kandungan spesifikasi anda, dan sistem stok atau inventori langsung anda — serta secara pilihan CRM anda supaya prospek panas mendarat di tempat pasukan jualan anda bekerja. Kami menyambungnya semasa pembinaan.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "零售与电商",
  title: "产品咨询智能体",
  cardSummary: "在 WhatsApp 上即时回答产品问题——库存、规格与价格——全部来自你自己的目录。",
  heroSubhead:
    "我们设计、构建并部署一个 WhatsApp AI 智能体，在问题送达的那一刻就作答——有没有货、规格如何、多少钱、哪个型号合适——直接取自你自己的目录与实时库存，让买家在几秒内得到答案，而不是趁你的员工忙碌时转投竞争对手。",
  snapshot: {
    valueLabel: "非常高",
    valueWhy:
      "大多数产品问题都来自 WhatsApp，而大多数买家不愿等待——即时且准确的答复，正是成交与流失之间的分水岭，昼夜皆然。",
    complexityLabel: "偏低",
    complexityWhy:
      "单一模型上的一个路由前端，分派到几个只读处理器；真正的工作是打通 WhatsApp、你的目录/库存与规格内容——无需视觉识别、无自动扣款、无繁重合规。",
    timeToLive: "约 2–4 周（单一目录）",
    bestFit: "在 WhatsApp 上应对产品问题的零售与电商中小企业",
  },
  outcomes: [
    "把即时、准确的答复转化为你如今因回复太慢而流失的销售",
    "接住团队来不及处理的营业时间外与高峰咨询",
    "减少报错价格与虚假库存——答案来自实时数据",
    "让团队从整天重复同样的十个答案中解放出来",
  ],
  impact: {
    manualLabel: "人工回答一条咨询",
    manualValue: "约 3–8 分钟（往往迟得多）",
    agentLabel: "使用智能体",
    agentValue: "约 5 秒，即时",
    saved:
      "每条咨询约节省 3–8 分钟——每月 800 条咨询约合 40–80 小时。但真正的收获是营收：即时答复的转化率远高于一小时后才到的答复，而 AI 成本约 RM 0.08，每天挽回一笔销售便已数倍覆盖其成本。",
    note: "为估算；实际时间与提升取决于目录规模与营业时间外的咨询量。",
  },
  businessProblem:
    "区域内的零售商或网店在 WhatsApp 上不断收到「ada stock?」「berapa harga?」「这个能用来…吗?」之类的问题。每一条都需要员工停下手中活儿、查目录、查库存、弄清客户指的是哪款产品，再打字回复——与此同时又有三条消息堆上来。高峰时它是瓶颈；营业时间外则是沉默，等到早上买家早已向先回复的人下了单。问题重复，但答案必须完全准确——报错价格或凭空的「有货」会让你损失金钱和信任。",
  manualWorkflow: {
    steps: [
      "阅读收到的 WhatsApp 问题",
      "弄清他们指的是哪款产品",
      "在目录中查规格与当前价格",
      "查库存——是否真的有货？",
      "打字回复，并处理后续追问",
    ],
    breaks:
      "瓶颈在于每个简单问题仍需要人来查询和回复——所以高峰时答复慢、营业时间外无人应答，而且每当价格或库存变动就会过时。",
  },
  agenticWorkflow: {
    narrative:
      "我们以路由（routing）方式构建：每条消息先被分类——这是查库存、问规格、问价格、要比较，还是该交给人处理？——再分派到对应意图的专门处理器，每个都从你的实时目录与库存作答，当这是真正的购买对话或超出范围时干净地上交给你的团队。",
    steps: [
      "对问题意图分类",
      "从目录中识别产品",
      "路由到正确的处理器（库存 / 规格 / 价格 / 比较）",
      "从实时目录与库存作答",
      "把热门线索或超范围问题上交给你的团队",
    ],
    inLabel: "客户在 WhatsApp 上提问",
    outLabel: "一条即时、准确的答复",
    loopAround: {
      index: 0,
      label: "随着对话转向，路由器会重新分类——规格问题可能变成价格再变成下单——每一轮都分派到正确的处理器。",
    },
    humanStep: "把准备下单的线索交给你的团队去成交",
  },
  workedExample: {
    inIcon: "whatsapp",
    inLabel: "客户消息",
    inMessage:
      "bro 这款 air fryer 5.5L 有货吗？要黑色的。price how much？可以 COD 到 KL 吗？",
    outLabel: "已发送即时答复",
    outLines: [
      "Air Fryer 5.5L（黑色）——有货，你的 PJ 仓库 12 台",
      "价格：RM 249（原价 RM 299）——促销至周日",
      "KL 可货到付款（COD）· 或配送 1–2 个工作日",
    ],
    outResult: "约 5 秒内答复 · 买家要求结账 → 交给你的团队",
    note: "每个数字都来自你的实时目录与库存，绝不臆测 · AI 成本约 RM 0.05–0.10 · 热门线索已标记供人成交。",
  },
  workflowDetail: [
    {
      title: "对意图分类",
      detail:
        "智能体阅读消息——马来语、英语、中文或人们实际混用的语言——弄清真正问的是什么：是否有货、规格如何、价格多少、两款产品如何比较，还是这是投诉或批量交易之类需要人处理的事。",
    },
    {
      title: "识别产品",
      detail:
        "它把消息对应到你目录中的真实商品——匹配型号、别称、颜色与简写——只有当它确实无法判断指的是哪款产品时，才提出一个简短的澄清问题。",
    },
    {
      title: "路由到正确的处理器",
      detail:
        "根据意图，它分派到专门的处理器——库存查询、规格查询、价格/促销答复，或并排比较——每个都针对那类问题调优，而不是一个笼统的回复。",
    },
    {
      title: "从实时数据作答",
      detail:
        "它从你的实时目录与当前库存作答——真实价格、真实规格、是否真的有货以及在哪里——因此它绝不报出过时价格，也不承诺没有的库存。",
    },
    {
      title: "抓住购买时机",
      detail:
        "当买家准备下单、想要批发价，或问了超出范围的问题时，它把对话的干净摘要交给你的团队去成交——咨询变成温热线索，而不是死胡同。",
    },
    {
      title: "交给你的团队",
      detail:
        "你的团队只介入值得他们花时间的对话——准备下单的线索与真正的边缘情况——而上下文已经收集好。",
    },
  ],
  howWeBuild:
    "因为它只从你的实时目录与当前库存作答，它无法报出昨天的价格，也无法承诺你已经卖掉的库存——答案要么正确、要么不发送。它绝不凭空编造无法引证的规格或折扣，绝不自行收款，并且每条咨询与答复都会留痕，因此你能清楚看到客户问了什么、智能体又回答了什么。",
  agentLoop: [
    "问题 + 你的实时目录、规格与库存",
    "对意图分类并从目录作答（MCP 工具）",
    "发送前核对答案确实引用了真实库存、规格与价格",
  ],
  integrations: [
    "WhatsApp Business API",
    "你的产品目录与规格内容",
    "实时库存 / 存货系统",
    "可选：你的 CRM 或线索收件箱，用于热门线索",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的路由工作流——一个轻量分类器把每一轮分派到几个专门处理器之一（库存 / 规格 / 价格 / 比较 / 上交），每个都通过进程内 MCP 服务器提供。",
    "上下文工程：你的目录结构、规格表与促销规则按产品检索并做提示缓存；实时库存在作答时读取，而非取自过时副本，因此答复反映此刻货架上真正有的东西。",
    "工具：只读的目录、规格与库存查询，外加一个比较助手——全部只读，因此不存在任何让一条咨询改动库存、价格或订单的路径；唯一的写入是创建线索记录。",
    "接地防护：答复被限制在工具返回的内容之内——智能体不得陈述任何它无法从实时数据引证的价格、规格或库存，从而封堵了凭空规格 / 幽灵库存的失败模式。",
    "不可信输入：客户消息被视为不可信（可抵御提示注入），因此精心构造的消息无法让它泄露内部数据或编造折扣；姓名与电话均按 PDPA/PDPC 处理。",
    "上交与恢复：分类器置信度低、出现购买信号或超范围的问题，都会带着对话摘要转入人工队列——每条不确定的路径都终结于人，而非一个自信的错误答案。",
    "可观测性与审计：每次分类、工具调用与答复都会留痕且可追溯，为你提供一份可检索的记录——客户问了什么、智能体又回答了什么——对合规与商品运营都有用。",
    "评估框架：一组真实历史咨询聊天的黄金集——杂乱的「rojak」、混合语言、产品含糊与刁钻问题——配合确定性检查（产品对、价格对、库存状态对）以及对语气与路由的 LLM 作为评审，每次改动在接触实时聊天前都做回归测试。",
  ],
  modelChoice: {
    approach: "单一模型，按任务分级",
    rationale:
      "路由一条咨询并作答共享同一段简短上下文，因此一个完善配置的单一模型加上一个廉价的分类器前端是正确选择——快速、每条咨询成本低、也易审计——而非多智能体设计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非一次问答交流；在这里，Haiku 分类器接入 Sonnet 处理器既更便宜也更快。",
  },
  models: [
    {
      task: "对意图分类并匹配产品",
      model: "Claude Haiku",
      why: "对绝大多数路由与商品匹配最便宜、最快，而这正是流量的主体。",
    },
    {
      task: "清晰回答规格、价格与库存",
      model: "Claude Sonnet",
      why: "对杂乱、混合语言的问题给出可靠、措辞得体的答复，并具备比较产品的细腻度。",
    },
    {
      task: "处理棘手的比较或异常问题",
      model: "Claude Sonnet",
      why: "接手分类器标记出来的更难案例——多产品比较、边缘的适配问题。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每条咨询",
        value: "约 RM 0.05–0.10",
        note: "一次简短的分类并作答交流，按 Claude 的 token 费率",
      },
      {
        label: "AI 用量——每月 3,000 条咨询",
        value: "约 RM 130–260 / 月",
        note: "随咨询量而变；Haiku 分类器让主体保持低成本",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入 WhatsApp、你的目录与库存系统约需数周；简短的需求梳理后给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比其接住的营业时间外咨询与挽回的销售，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于咨询长度与每月业务量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  evaluation: [
    "先做确定性检查——产品对、当前价格对、库存状态对",
    "自我验证——答复必须在发送前引用实时目录与库存",
    "业务 KPI——营业时间外回答的咨询、答复准确率、以及交给团队的热门线索",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量你当前的 WhatsApp 咨询处理——问题多快得到答复、有多少答错（价格过时、库存不对）、有多少营业时间外的咨询被漏掉。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否正确回答了问题？",
        metrics: [
          {
            name: "答复准确率（对比人工）",
            measures: "在留出聊天上，答复与正确的产品、价格及库存状态一致",
            target: "≥ 97%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "咨询是否被稳妥地路由与处理？",
        metrics: [
          {
            name: "意图路由正确",
            measures: "库存 / 规格 / 价格 / 比较 / 上交被正确分类",
            target: "≥ 95%",
          },
          {
            name: "热门线索已上交",
            measures: "准备下单与超范围的对话都交给人——无一遗漏",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个答复是否正确？",
        metrics: [
          {
            name: "产品匹配准确率",
            measures: "消息映射到正确的目录商品",
            target: "≥ 97%",
          },
          {
            name: "价格与库存正确性",
            measures: "每一个报出的价格与库存都与作答时的实时数据一致",
            target: "≥ 99%",
          },
          {
            name: "不凭空捏造规格或库存",
            measures: "绝不陈述任何无法从你的数据引证的规格、价格或库存",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于真实历史咨询聊天黄金集（含杂乱与刁钻案例）上超过人工基线并达到这些 GPA 目标之前，它不会回复任何实时客户。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的 WhatsApp 咨询聊天上做概念验证，在一个渠道或目录以员工监督试运行，然后正式上线。聚焦单一目录的落地只需数周。",
  ctaTitle: "整天在 WhatsApp 上回答同样的产品问题？",
  ctaDescription:
    "告诉我们你的目录规模与咨询量，我们会诚实告诉你产品咨询智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "它如何避免报错价格或虚假库存？",
      answer:
        "它只通过连接的工具从你的实时目录与库存作答，并被约束为不得陈述任何无法从这些数据引证的价格、规格或库存。如果没有把握，它会追问或上交，而不是臆测。",
    },
    {
      question: "它能处理马来语、英语和中文——以及混合消息吗？",
      answer:
        "可以。它按马来西亚人实际的输入方式构建——马来语、英语、中文及「rojak」混合——并在同一段对话中清晰作答。",
    },
    {
      question: "它会接单或收款吗？",
      answer:
        "不会——这个智能体负责回答问题，并把准备下单的线索交给你的团队。如果你还希望它端到端地记录订单，那是我们的订单处理智能体，与这个恰好搭配。",
    },
    {
      question: "它对接什么？",
      answer:
        "WhatsApp Business、你的产品目录与规格内容，以及你的实时库存或存货系统——并可选对接你的 CRM，让热门线索落到销售团队工作的地方。我们在构建时对接。",
    },
  ],
};

export const productEnquiry: AgentUseCase = {
  structure: {
    slug: "product-enquiry",
    status: "available",
    pattern: "routing",
    route: "solutions/agent-use-cases/product-enquiry",
    heroImage: "/images/agent-use-cases/product-enquiry-hero.jpg",
    cardImage: "/images/agent-use-cases/product-enquiry-card.jpg",
    relatedUseCases: ["order-processing", "support-triage"],
    trackLocation: "agent_use_case_product_enquiry",
    valueScore: 5,
    complexityScore: 2,
  },
  content: { en, ms, zh },
};
